package dev.lingvo.site.components.editor

import kotlinx.browser.document
import kotlinx.browser.window
import org.w3c.dom.Element
import org.w3c.dom.HTMLElement
import org.w3c.dom.HTMLTextAreaElement
import org.w3c.dom.events.EventListener
import org.w3c.dom.events.KeyboardEvent

private const val EDITOR_FOCUSABLE_SELECTOR =
    "button:not(:disabled), input:not(:disabled), textarea:not(:disabled), " +
        "select:not(:disabled), a[href], [tabindex]:not([tabindex='-1'])"

internal fun currentEditorTextEntryContext(): EditorTextEntryContext {
    val active = document.activeElement ?: return EditorTextEntryContext.NONE
    if (active.closest("[data-editor-text-context='target']") != null) {
        return EditorTextEntryContext.TARGET_TEXT
    }
    if (active.closest("[data-editor-text-context='utility']") != null) {
        return EditorTextEntryContext.UTILITY_TEXT
    }
    return if (active.isTextEntry()) {
        EditorTextEntryContext.OTHER_TEXT
    } else {
        EditorTextEntryContext.NONE
    }
}

internal fun addEditorKeydownListener(listener: EventListener) {
    window.addEventListener("keydown", listener)
}

internal fun removeEditorKeydownListener(listener: EventListener) {
    window.removeEventListener("keydown", listener)
}

internal fun addEditorResizeListener(listener: EventListener) {
    window.addEventListener("resize", listener)
}

internal fun removeEditorResizeListener(listener: EventListener) {
    window.removeEventListener("resize", listener)
}

internal fun currentEditorViewportWidth(): Int = window.innerWidth

internal fun readEditorQueueScrollTop(): Double? =
    (document.querySelector("[data-editor-queue-scroll]") as? HTMLElement)
        ?.takeIf(HTMLElement::isEditorConnected)
        ?.scrollTop

internal fun readEditorWorkspaceElement(): Element? =
    document.querySelector("[data-editor-workspace]")

internal fun readEditorScrollerElement(): Element? =
    document.querySelector("[data-editor-scroll]")

internal fun readEditorWorkspaceClientWidth(): Double? =
    (document.querySelector("[data-editor-workspace]") as? HTMLElement)
        ?.takeIf(HTMLElement::isEditorConnected)
        ?.clientWidth
        ?.toDouble()

internal fun readEditorScrollerClientWidth(): Double? =
    (document.querySelector("[data-editor-scroll]") as? HTMLElement)
        ?.takeIf(HTMLElement::isEditorConnected)
        ?.clientWidth
        ?.toDouble()

internal fun readEditorActiveFocusRef(capturedFocus: EditorCapturedFocusRegistry): EditorFocusRef? {
    val active = document.activeElement as? HTMLElement ?: return null
    if (!active.isEditorConnected()) return null
    val workspace = active.closest("[data-editor-workspace]") as? HTMLElement
    val utility = active.closest("[data-editor-utility]") as? HTMLElement
    if (workspace == null && utility == null) return null
    if (!active.isEditorUsable()) return null

    val search = active.closest("[data-editor-search='true']")
    if (search != null) return EditorFocusRef.Search

    val row = active.closest("[data-editor-queue-row]") as? HTMLElement
    if (row != null) {
        row.getAttribute("data-editor-key-id")?.takeIf(String::isNotBlank)?.let {
            return EditorFocusRef.QueueRow(it)
        }
        row.id.removePrefix(KEY_ROW_ID_PREFIX).takeIf { it != row.id && it.isNotBlank() }?.let {
            return EditorFocusRef.QueueRow(it)
        }
    }

    val target = active.closest("[data-editor-target]") as? HTMLElement
    if (target != null) {
        val keyId = target.getAttribute("data-editor-key-id")?.takeIf(String::isNotBlank)
        if (keyId != null) {
            val targetRef = EditorFocusRef.Target(
                keyId = keyId,
                pluralCategory = target.getAttribute("data-editor-plural-category")
                    ?.takeIf(String::isNotBlank)
                    ?.let { runCatching { dev.lingvo.shared.PluralCategory.valueOf(it) }.getOrNull() },
                locale = target.getAttribute("data-editor-locale")?.takeIf(String::isNotBlank),
            )
            val textarea = active as? HTMLTextAreaElement
            if (textarea != null) {
                val maximum = textarea.value.length
                val start = (textarea.selectionStart ?: 0).coerceIn(0, maximum)
                val end = (textarea.selectionEnd ?: start).coerceIn(0, maximum)
                return EditorFocusRef.TargetSnapshot(
                    target = targetRef,
                    selectionStart = start,
                    selectionEnd = end,
                    selectionDirection = textarea.asDynamic().selectionDirection as? String,
                )
            }
            return targetRef
        }
    }

    active.id
        .takeIf { it in EDITOR_PERSISTENT_FOCUS_IDS }
        ?.let { return EditorFocusRef.ElementId(it) }
    return if (workspace != null || utility != null) {
        capturedFocus.capture(active)?.let(EditorFocusRef::Captured)
    } else {
        null
    }
}

private val EDITOR_PERSISTENT_FOCUS_IDS = setOf(
    EDITOR_SEARCH_ID,
    EDITOR_TARGET_ID,
    KEY_HEADER_ID,
    KEY_SCROLLER_ID,
    EDITOR_HEADER_ASSIST_TRIGGER_ID,
    EDITOR_INLINE_ASSIST_TRIGGER_ID,
    EDITOR_DETAILS_TRIGGER_ID,
    EDITOR_FILTERS_TRIGGER_ID,
    EDITOR_OVERFLOW_TRIGGER_ID,
    EDITOR_UTILITY_CLOSE_ID,
    EDITOR_CONFLICT_TARGET_ID,
    EDITOR_CLEAR_MODAL_INITIAL_FOCUS_ID,
    EDITOR_SHORTCUTS_MODAL_INITIAL_FOCUS_ID,
    EDITOR_BULK_MODAL_INITIAL_FOCUS_ID,
    EDITOR_SUMMARY_TOGGLE_ID,
    EDITOR_MODAL_SURFACE_ID,
    EDITOR_SHEET_SURFACE_ID,
)

internal fun captureShortcutInvoker(capturedFocus: EditorCapturedFocusRegistry): EditorFocusRef {
    return readEditorActiveFocusRef(capturedFocus)
        ?: EditorFocusRef.ElementId(EDITOR_OVERFLOW_TRIGGER_ID)
}

internal fun editorFocusableElements(surfaceId: String): List<HTMLElement> {
    val surface = document.getElementById(surfaceId) as? HTMLElement ?: return emptyList()
    val nodes = surface.querySelectorAll(EDITOR_FOCUSABLE_SELECTOR)
    return buildList {
        for (index in 0 until nodes.length) {
            val element = nodes.item(index) as? HTMLElement ?: continue
            if (element.isEditorUsable()) add(element)
        }
    }
}

internal fun trapEditorFocus(
    event: KeyboardEvent,
    workspace: EditorWorkspaceState,
    capturedFocus: EditorCapturedFocusRegistry,
    onRequestFocus: (EditorFocusRef, EditorFocusRef?) -> Unit,
) {
    val surfaceId = when {
        workspace.modal != EditorModalSurface.NONE -> EDITOR_MODAL_SURFACE_ID
        workspace.utility != EditorUtilitySurface.NONE &&
            workspace.renderedLayout() == EditorRenderedLayout.COMPACT -> EDITOR_SHEET_SURFACE_ID
        else -> null
    } ?: return
    val focusable = editorFocusableElements(surfaceId)
    if (focusable.isEmpty()) return
    val active = document.activeElement as? HTMLElement
    val activeIndex = focusable.indexOfFirst { it === active }
    val destinationIndex = focusTrapWrapIndex(
        activeIndex = activeIndex,
        focusableCount = focusable.size,
        reverse = event.shiftKey,
    ) ?: return
    event.preventDefault()
    event.stopPropagation()
    val token = capturedFocus.capture(focusable[destinationIndex]) ?: return
    onRequestFocus(
        EditorFocusRef.Captured(token),
        EditorFocusRef.ElementId(surfaceId),
    )
}

internal fun readEditorEvidenceHookCounts(): EditorEvidenceHookCounts {
    fun count(selector: String): Int = document.querySelectorAll(selector).length
    return EditorEvidenceHookCounts(
        workspace = document.querySelectorAll("[data-editor-workspace]").length,
        probe = document.querySelectorAll("[data-editor-measurement-probe]").length,
        queue = count("[data-editor-queue]"),
        queueScroll = count("[data-editor-queue-scroll]"),
        filters = count("[data-editor-filter-surface]"),
        editorScroll = count("[data-editor-scroll]"),
        editorContent = count("[data-editor-content]"),
        source = count("[data-editor-source]"),
        target = count("[data-editor-target]"),
        pluralTabs = count("[data-editor-plural-tabs]"),
        pluralPanel = count("[data-editor-plural-panel]"),
        pluralPreview = count("[data-editor-plural-preview]"),
        utility = count("[data-editor-utility]"),
        compactSummary = count("[data-editor-compact-summary]"),
        utilityBody = count("[data-editor-utility-body]"),
        details = count("[data-editor-details]"),
        commentComposer = count("[data-editor-comment-composer]"),
        actionBar = count("[data-editor-action-bar]"),
        primary = count("[data-editor-primary='true']"),
        conflict = count("[data-editor-conflict='true']"),
        lTabs = count("[data-ltabs]"),
        lTabsPanels = count("[data-ltabs-panel]"),
    )
}

internal fun readWorkspaceLayoutHook(): String? =
    (document.querySelector("[data-editor-workspace]") as? HTMLElement)
        ?.getAttribute("data-editor-layout")

internal fun editorMeasurementCommitIsValid(
    disposition: EditorMeasuredLayoutDisposition,
    measurementId: Long,
    acceptedMeasurementId: Long?,
    expectedSettledLayoutHook: String,
): Boolean {
    val hooks = readEditorEvidenceHookCounts()
    val workspace = document.querySelector("[data-editor-workspace]") as? HTMLElement
    val probe = document.querySelector("[data-editor-measurement-probe]") as? HTMLElement
    val probeId = probe?.getAttribute("data-editor-measurement-probe")?.toLongOrNull()
    if (hooks.workspace != 1 || workspace == null) return false
    return when (disposition) {
        EditorMeasuredLayoutDisposition.MEASURING ->
            hooks.probe == 1 && probeId == measurementId &&
                workspace.getAttribute("data-editor-layout") == "measuring"

        EditorMeasuredLayoutDisposition.WORKBENCH,
        EditorMeasuredLayoutDisposition.COMPACT_FALLBACK,
        -> settledEditorLayoutHookIsValid(hooks, expectedSettledLayoutHook) &&
            acceptedMeasurementId == measurementId

        EditorMeasuredLayoutDisposition.NOT_APPLICABLE -> false
    }
}

internal fun editorAbandonedFallbackCommitIsValid(
    disposition: EditorMeasuredLayoutDisposition,
    expectedSettledLayoutHook: String,
): Boolean {
    if (
        disposition != EditorMeasuredLayoutDisposition.WORKBENCH &&
        disposition != EditorMeasuredLayoutDisposition.COMPACT_FALLBACK
    ) {
        return false
    }
    return settledEditorLayoutHookIsValid(
        hooks = readEditorEvidenceHookCounts(),
        expectedSettledLayoutHook = expectedSettledLayoutHook,
    )
}

private fun settledEditorLayoutHookIsValid(
    hooks: EditorEvidenceHookCounts,
    expectedSettledLayoutHook: String,
): Boolean = hooks.workspace == 1 &&
    hooks.probe == 0 &&
    readWorkspaceLayoutHook() == expectedSettledLayoutHook

internal fun lockEditorDocumentOverflow(): () -> Unit {
    val body = document.body
    val root = document.documentElement as? HTMLElement
    val previousBodyOverflow = body?.style?.getPropertyValue("overflow").orEmpty()
    val previousRootOverflow = root?.style?.getPropertyValue("overflow").orEmpty()
    body?.style?.setProperty("overflow", "hidden")
    root?.style?.setProperty("overflow", "hidden")
    return {
        restoreEditorStyle(body, "overflow", previousBodyOverflow)
        restoreEditorStyle(root, "overflow", previousRootOverflow)
    }
}

private fun restoreEditorStyle(element: HTMLElement?, property: String, value: String) {
    if (value.isBlank()) element?.style?.removeProperty(property)
    else element?.style?.setProperty(property, value)
}
